import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Input, Button, Row, Col,
    Breadcrumb, message, Typography, Select, Tooltip, DatePicker, Empty
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    HomeOutlined, BarChartOutlined, SearchOutlined, FilterOutlined, ClearOutlined,
    PrinterOutlined, FileExcelOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildItemsReportPDF } from "../../../utils/itemsReportPdfUtils";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];

const InventoryItemsReport = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter state (server-side)
    const [search, setSearch] = useState('');
    const [filterCat, setFilterCat] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [applied, setApplied] = useState({});
    // Report is generated on demand: no data until the user applies a filter.
    const [hasSearched, setHasSearched] = useState(false);

    // Print preview state
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [includeFilters, setIncludeFilters] = useState(false);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await api.get('/inventory/category/list.php?page=1&limit=100&active_only=1');
            setCategories(res.data.dataset || []);
        } catch {
            // Non-critical: filter dropdown simply stays empty
        }
    }, []);

    const fetchItems = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get('/reports/inventory/items.php', {
                params: {
                    search: filters.search || '',
                    ic_id: filters.ic_id || 0,
                    status: filters.status || '',
                    start_date: filters.start_date || '',
                    end_date: filters.end_date || '',
                },
            });
            setItems(res.data.dataset || []);
        } catch {
            message.error("Failed to fetch inventory items report.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only the category dropdown is pre-loaded; the report data itself is
        // fetched when the user applies a filter.
        fetchCategories();
    }, [fetchCategories]);

    // True when the user has actually set at least one filter value.
    const hasAnyFilter = Boolean(
        search.trim() ||
        (filterCat !== undefined && filterCat !== null) ||
        (filterStatus !== undefined && filterStatus !== null && String(filterStatus).trim() !== '') ||
        (dateRange && dateRange[0])
    );

    const handleFilter = () => {
        // A report is generated only when a filter has been set and applied.
        if (!hasAnyFilter) {
            message.warning("Set at least one filter before applying.");
            return;
        }
        const filters = {
            search: search.trim(),
            ic_id: filterCat || 0,
            status: filterStatus || '',
            start_date: dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : '',
            end_date: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : '',
        };
        setApplied(filters);
        setHasSearched(true);
        fetchItems(filters);
    };

    const handleClear = () => {
        setSearch('');
        setFilterCat(undefined);
        setFilterStatus('');
        setDateRange(null);
        setApplied({});
        // Clearing returns to the pre-search state: no data until Apply again.
        setHasSearched(false);
        setItems([]);
    };

    const buildFilterLabels = () => ({
        search: applied.search || '',
        category: applied.ic_id
            ? (categories.find((c) => c.ic_id === applied.ic_id)?.ic_name || '')
            : '',
        status: applied.status === 'active' ? 'Active' : (applied.status === 'inactive' ? 'Inactive' : ''),
        dateRange: applied.start_date && applied.end_date
            ? `${applied.start_date} to ${applied.end_date}`
            : '',
    });

    const handlePrint = async () => {
        if (items.length === 0) {
            message.warning("No data to print.");
            return;
        }
        setPrintVisible(true);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const doc = await buildItemsReportPDF({
                items,
                includeFilters,
                filters: buildFilterLabels(),
            });
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleIncludeFiltersChange = async (checked) => {
        setIncludeFilters(checked);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const doc = await buildItemsReportPDF({
                items,
                includeFilters: checked,
                filters: buildFilterLabels(),
            });
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const doc = await buildItemsReportPDF({
                items,
                includeFilters,
                filters: buildFilterLabels(),
            });
            doc.save(`inventory-items-report-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const exportToExcel = () => {
        if (items.length === 0) {
            message.warning("No data to export.");
            return;
        }

        // Group items by category: category heading row, then its items
        const groups = {};
        items.forEach((it) => {
            const cat = it.category_name || "Uncategorized";
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(it);
        });
        const categoryNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

        const aoa = [["Category", "Item Name", "Available Qty", "Min Order Qty"]];
        categoryNames.forEach((cat) => {
            aoa.push([cat]); // Category heading row
            groups[cat].forEach((it) => {
                aoa.push(["", it.item_name || "", it.available_qty, it.reorder_qty]);
            });
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!cols"] = [{ wch: 22 }, { wch: 40 }, { wch: 16 }, { wch: 16 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory Items");
        XLSX.writeFile(wb, `inventory-items-report-${dayjs().format("YYYYMMDD-HHmm")}.xlsx`);
        message.success("Excel exported successfully.");
    };

    const columns = [
        {
            title: 'Item Name', dataIndex: 'item_name', key: 'item_name',
            sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
        },
        {
            title: 'Category', dataIndex: 'category_name', key: 'category_name',
            sorter: (a, b) => (a.category_name || '').localeCompare(b.category_name || ''),
            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Available Qty', dataIndex: 'available_qty', key: 'available_qty', align: 'center',
            sorter: (a, b) => (a.available_qty || 0) - (b.available_qty || 0),
        },
        {
            title: 'Min Order Qty', dataIndex: 'reorder_qty', key: 'reorder_qty', align: 'center',
            sorter: (a, b) => (a.reorder_qty || 0) - (b.reorder_qty || 0),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Reports' },
                                { title: 'Inventory' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <BarChartOutlined /> Inventory Report
                        </h1>
                        <p className="fluid-page-subtitle">Stock items with category, available and minimum order quantities</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Tooltip title="Print report">
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={handlePrint}
                                    className="hero-action-btn"
                                >
                                    Print
                                </Button>
                            </Tooltip>
                            <Tooltip title="Export to Excel">
                                <Button
                                    icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                    onClick={exportToExcel}
                                    className="hero-action-btn"
                                >
                                    Export Excel
                                </Button>
                            </Tooltip>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Code, name, description"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Category:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterCat}
                                    onChange={setFilterCat}
                                    placeholder="All Categories"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map((cat) => <Option key={cat.ic_id} value={cat.ic_id}>{cat.ic_name}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    allowClear
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Created Date:</div>
                                <DatePicker.RangePicker
                                    style={{ width: "100%" }}
                                    value={dateRange}
                                    onChange={(dates) => setDateRange(dates)}
                                    placeholder={["Start date", "End date"]}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={24} lg={6}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {!hasSearched ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Set at least one filter and click Apply to generate the report."
                            style={{ padding: "48px 0" }}
                        />
                    ) : (
                        <div className="fluid-table">
                            <Table
                                size="small"
                                columns={columns}
                                dataSource={items}
                                rowKey="invi_id"
                                loading={loading}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                }}
                                scroll={{ x: 1100 }}
                            />
                        </div>
                    )}
                </div>
            </Content>

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title={`Inventory Report${applied.search || applied.ic_id || applied.status || applied.start_date ? " (Filtered)" : ""}`}
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                includeFilters={includeFilters}
                onIncludeFiltersChange={handleIncludeFiltersChange}
                onDownload={handleDownloadPDF}
            />

            <Footerdiv />
        </Layout>
    );
};

export default InventoryItemsReport;
